import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Calendar, CheckCircle, ChevronDown, FileCheck, GraduationCap, LayoutDashboard, MessageSquare, Search, Zap, Briefcase, User, Users } from 'lucide-react';
import { FeatureGate, SubFeatureGate, useFeatures } from './FeatureGate';
import '../pages/StudentDashboard.css';

const AI_AGENT_TABS = [
  { sub: 'ai_resume_scorer', label: 'AI Resume Scorer', path: '/ai-agents/resume-scorer', Icon: FileCheck },
  { sub: 'ai_counsellor', label: 'AI Counsellor', path: '/ai-agents/counsellor', Icon: GraduationCap },
  { sub: 'ai_interviewer', label: 'AI Interviewer', path: '/ai-agents/interviewer', Icon: Briefcase },
  { sub: 'ai_tutor', label: 'AI Tutor', path: '/ai-agents/tutor', Icon: GraduationCap },
];

const AI_AGENTS_OPEN_KEY = 'aiAgentsNavOpen';

function StudentSidebar({ onScheduleClick, onDashboardClick, onSettingsClick, onProfileClick, onDiscoverClick, onCompletedClick, onLMSClick, onPartnerCoursesClick, onDiscussionsClick, onChatClick, onJobsClick, onPlatformFeedbackClick, activeTab, unreadCount = 0, hasNewJobs = false, aiAgentEnabled = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSubEnabled } = useFeatures();
  // The AI Agents page is empty unless at least one agent sub-feature is
  // enabled, so only show its sidebar entry when one is on (the master
  // toggle alone must not expose an empty page).
  const anyAgentSubEnabled = ['ai_interviewer', 'ai_resume_scorer', 'ai_tutor', 'ai_counsellor']
    .some((s) => isSubEnabled('ai_agents', s));

  // AI Agents submenu — collapsed elsewhere, auto-expanded on any AI Agents page.
  const onAiAgentsPage = location.pathname === '/ai-agents' || location.pathname.startsWith('/ai-agents/');
  const [aiAgentsOpen, setAiAgentsOpen] = useState(() => {
    try {
      return localStorage.getItem(AI_AGENTS_OPEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    if (onAiAgentsPage) setAiAgentsOpen(true);
  }, [onAiAgentsPage]);

  const toggleAiAgents = (next) => {
    const open = typeof next === 'boolean' ? next : !aiAgentsOpen;
    setAiAgentsOpen(open);
    try {
      localStorage.setItem(AI_AGENTS_OPEN_KEY, open ? '1' : '0');
    } catch (e) {
      /* storage unavailable (private mode) — state still applies for this visit */
    }
  };

  const handleDashboardClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard');
    } else if (onDashboardClick) {
      onDashboardClick();
    }
  };

  const handleProfileClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showSettings: true } });
    } else if (onProfileClick) {
      onProfileClick();
    } else if (onSettingsClick) {
      onSettingsClick();
    }
  };

  const handleDiscoverClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showDiscover: true } });
    } else if (onDiscoverClick) {
      onDiscoverClick();
    }
  };

  const handleScheduleClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showSessions: true } });
    } else if (onScheduleClick) {
      onScheduleClick();
    }
  };

  const handleDiscussionsClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showDiscussions: true } });
    } else if (onDiscussionsClick) {
      onDiscussionsClick();
    }
  };

  const handleChatClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showChat: true } });
    } else if (onChatClick) {
      onChatClick();
    }
  };

  const handleCompletedClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showCompleted: true } });
    } else if (onCompletedClick) {
      onCompletedClick();
    }
  };

  const handleLMSClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showLMS: true } });
    } else if (onLMSClick) {
      onLMSClick();
    }
  };

  const handleJobsClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showJobs: true } });
    } else if (onJobsClick) {
      onJobsClick();
    }
  };

  const handlePlatformFeedbackClick = () => {
    if (location.pathname !== '/student-dashboard') {
      navigate('/student-dashboard', { state: { showPlatformFeedback: true } });
    } else if (onPlatformFeedbackClick) {
      onPlatformFeedbackClick();
    }
  };

  return (
    <aside className="stu-sidebar">
      <nav className="stu-sidebar-nav">
        <button
          onClick={handleDashboardClick}
          className={`stu-nav-item ${location.pathname === '/student-dashboard' && activeTab === 'explore' ? 'stu-active' : ''}`}
        >
          <LayoutDashboard size={18} /> Dashboard
        </button>

        <FeatureGate feature="lms">
        <FeatureGate feature="navigatelabs">
        <button
          onClick={handleLMSClick}
          className={`stu-nav-item ${activeTab === 'lms' ? 'stu-active' : ''}`}
        >
          <BookOpen size={18} /> LMS
        </button>
        </FeatureGate>
        </FeatureGate>

        <FeatureGate feature="jobs">
        <SubFeatureGate feature="jobs" sub="jobs_view">
        <button
          onClick={handleJobsClick}
          className={`stu-nav-item ${activeTab === 'jobs' ? 'stu-active' : ''}`}
        >
          <Briefcase size={18} />
          <span style={{ flex: 1, textAlign: 'left' }}>Jobs</span>
          {hasNewJobs && (
            <span style={{
              width: '8px',
              height: '8px',
              background: '#ef4444',
              borderRadius: '50%',
              display: 'inline-block',
              boxShadow: '0 0 0 2px #ffffff'
            }} title="New placement drive available!" />
          )}
        </button>
        </SubFeatureGate>
        </FeatureGate>

        {aiAgentEnabled && (
          <>
            <FeatureGate feature="ai_agents">
            {anyAgentSubEnabled && (
            <div className="stu-nav-group">
              <div className={`stu-nav-item stu-nav-parent ${onAiAgentsPage ? 'stu-active' : ''}`}>
                <button
                  className="stu-nav-parent-main"
                  onClick={() => { toggleAiAgents(true); navigate('/ai-agents'); }}
                >
                  <Zap size={18} />
                  <span style={{ flex: 1, textAlign: 'left' }}>AI Agents</span>
                </button>
                <button
                  className="stu-nav-chevron"
                  onClick={() => toggleAiAgents()}
                  aria-label="Toggle AI Agents submenu"
                  aria-expanded={aiAgentsOpen}
                  title="Toggle AI Agents submenu"
                >
                  <ChevronDown
                    size={16}
                    style={{ transform: aiAgentsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }}
                  />
                </button>
              </div>

              {aiAgentsOpen && (
                <div className="stu-submenu">
                  {AI_AGENT_TABS.map(({ sub, label, path, Icon }) => (
                    <SubFeatureGate key={sub} feature="ai_agents" sub={sub}>
                      <button
                        onClick={() => navigate(path)}
                        className={`stu-nav-subitem ${location.pathname === path ? 'stu-active' : ''}`}
                      >
                        <Icon size={16} />
                        <span>{label}</span>
                      </button>
                    </SubFeatureGate>
                  ))}
                </div>
              )}
            </div>
            )}
            </FeatureGate>
            <FeatureGate feature="sidebar_interviewer">
            <button
              onClick={() => navigate('/ai-interviewer')}
              className={`stu-nav-item ${location.pathname === '/ai-interviewer' ? 'stu-active' : ''}`}
            >
              <Briefcase size={18} /> AI Interviewer
            </button>
            </FeatureGate>
          </>
        )}

        <FeatureGate feature="schedule">
        <SubFeatureGate feature="schedule" sub="schedule_view">
        <button
          id="student-engagements-tab"
          onClick={handleScheduleClick}
          className={`stu-nav-item ${activeTab === 'sessions' ? 'stu-active' : ''}`}
        >
          <Calendar size={18} /> My Schedule
        </button>
        </SubFeatureGate>
        </FeatureGate>

        <FeatureGate feature="discussions">
        <button
          onClick={handleDiscussionsClick}
          className={`stu-nav-item ${(activeTab === 'discussions' || location.pathname === '/discussions') ? 'stu-active' : ''}`}
        >
          <Users size={18} /> Groups
        </button>
        </FeatureGate>

        <FeatureGate feature="messages">
        <SubFeatureGate feature="messages" sub="messages_view">
        <button
          onClick={handleChatClick}
          className={`stu-nav-item ${(activeTab === 'chat' || location.pathname === '/chat') ? 'stu-active' : ''}`}
        >
          <MessageSquare size={18} />
          <span style={{ flex: 1, textAlign: 'left' }}>Messages</span>
          {unreadCount > 0 && (
            <span style={{
              background: '#ef4444',
              color: 'white',
              fontSize: '11px',
              fontWeight: '600',
              padding: '2px 8px',
              borderRadius: '999px',
              minWidth: '20px',
              textAlign: 'center'
            }}>
              {unreadCount}
            </span>
          )}
        </button>
        </SubFeatureGate>
        </FeatureGate>

        <FeatureGate feature="platform_feedback">
        <SubFeatureGate feature="platform_feedback" sub="platform_feedback_view">
        <button
          onClick={handlePlatformFeedbackClick}
          className={`stu-nav-item ${activeTab === 'platform_feedback' ? 'stu-active' : ''}`}
        >
          <MessageSquare size={18} /> Platform Feedback
        </button>
        </SubFeatureGate>
        </FeatureGate>

        <button
          onClick={handleProfileClick}
          className={`stu-nav-item ${(activeTab === 'settings' || activeTab === 'profile') ? 'stu-active' : ''}`}
        >
          <User size={18} /> Profile
        </button>
      </nav>
    </aside>
  );
}

export default StudentSidebar;
